/**
 * ChatHeader Component
 * Header della chat con info contatto e presenza
 */

import { memo, useState, type ComponentProps } from 'react';
import { ArrowLeft, Video, Sparkles, X, CheckSquare, Bot, StickyNote, Search, Users, Info, PanelRightOpen, PanelRightClose } from 'lucide-react';
import { AnalyzeWithAIModal } from './AnalyzeWithAIModal';
import { WhatsAppMeetModal } from './WhatsAppMeetModal';
import { ChatAvatar } from './ChatAvatar';
import { useProfilePicture } from '@/lib/hooks/whatsapp/useProfilePicture';
import { ConversationStatusBadge } from '../shared/ConversationStatusBadge';
import { OperatorAssignPicker } from '../shared/OperatorAssignPicker';
import { LabelPicker } from '../shared/LabelPicker';
import { ClientBadge } from '../shared/ClientBadge';
import { RecontactHeaderButton } from './RecontactHeaderButton';
import { ProviderBadge } from '../shared/ProviderBadge';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useGroupParticipantNames } from '@/lib/hooks/whatsapp/useMentionNames';
import { useMessageSelection } from '../context/MessageSelectionContext';
import type { ChatOverview, ContactPresence } from '@/lib/types/whatsappChat';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { useGroupRealtime } from '../group/useGroupRealtime';
import { isCloudService } from '@/lib/services/whatsapp/newChatLogic';

interface ChatHeaderProps {
  chat: ChatOverview;
  presence?: ContactPresence;
  onBack?: () => void;
  onInfoClick?: () => void;
  onAgentClick?: () => void;
  onNotesClick?: () => void;
  notesCount?: number;
  onSearchClick?: () => void;
  onDebugClick?: () => void;
  onForwardToTeamChat?: (messages: import('@/lib/types/whatsappChat').Message[]) => void;
  onPanelToggle?: () => void;
  isPanelOpen?: boolean;
  /** Spazio stretto (drawer compatto o colonna sola): pulsanti solo icona. */
  compact?: boolean;
}

function PresenceText({ presence }: { presence?: ContactPresence }) {
  if (!presence) return null;

  if (presence.status === 'typing') {
    return <span className="text-[#2383E2]">Sta scrivendo...</span>;
  }

  if (presence.status === 'recording') {
    return <span className="text-[#2383E2]">Sta registrando...</span>;
  }

  if (presence.status === 'online') {
    return <span className="text-green-500">Online</span>;
  }

  if (presence.lastSeen) {
    const date = new Date(presence.lastSeen);
    const timeStr = date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return <span>Ultimo accesso {timeStr}</span>;
  }

  return null;
}

export const ChatHeader = memo(function ChatHeader({
  chat,
  presence,
  onBack,
  onInfoClick,
  onAgentClick,
  onNotesClick,
  notesCount = 0,
  onSearchClick,
  onDebugClick,
  onForwardToTeamChat,
  onPanelToggle,
  isPanelOpen,
  compact = false,
}: ChatHeaderProps) {
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [showMeetModal, setShowMeetModal] = useState(false);

  // P2: Selettori individuali
  const wahaConfig = useWhatsAppChatStore(s => s.wahaConfig);
  const messages = useWhatsAppChatStore(s => s.messages);
  const chatMeta = useWhatsAppChatMetaStore(s => s.chatMeta);
  const orgMembers = useWhatsAppChatMetaStore(s => s.orgMembers);
  const upsertMeta = useWhatsAppChatMetaStore(s => s.upsertMeta);

  const participantNames = useGroupParticipantNames(chat.isGroup ? chat.id : undefined, !!chat.isGroup);
  // Gruppi solo sui numeri Standard (WAHA); il broadcast del core aggiorna i nomi dei gruppi in lista.
  const groupsAvailable = !isCloudService(wahaConfig?.serviceName);
  useGroupRealtime(wahaConfig?.organizationId, undefined, groupsAvailable);

  const meta = chatMeta.get(chat.id);
  const currentStatus = meta?.conversation_status || 'da_gestire';
  const assignedOperatorId = meta?.assigned_operator_id || null;

  const handleStatusChange = (status: 'da_gestire' | 'in_gestione' | 'chiusa') => {
    if (wahaConfig?.organizationId) upsertMeta(chat.id, { conversation_status: status }, wahaConfig.organizationId);
  };

  const handleOperatorChange = (userId: string | null) => {
    if (wahaConfig?.organizationId) upsertMeta(chat.id, { assigned_operator_id: userId, assigned_at: userId ? new Date().toISOString() : null }, wahaConfig.organizationId);
  };

  // Message selection context
  const {
    isSelectionMode,
    selectedMessageIds,
    enterSelectionMode,
    exitSelectionMode,
    selectAll,
    getSelectedMessages,
  } = useMessageSelection();

  // Feature flag: AI Analysis enabled only for specific organizations
  const aiAnalysisEnabledOrgs = (import.meta.env.VITE_AI_ANALYSIS_ENABLED_ORGS || '').split(',').filter(Boolean);
  const isAiAnalysisEnabled = wahaConfig?.organizationId && aiAnalysisEnabledOrgs.includes(wahaConfig.organizationId);

  // Gestione click su bottone IA
  const handleAiButtonClick = () => {
    if (isSelectionMode) {
      // Se già in selection mode, apri il modal
      if (selectedMessageIds.size > 0) {
        setShowAnalyzeModal(true);
      }
    } else {
      // Entra in selection mode
      enterSelectionMode();
    }
  };

  // Gestione annulla selezione
  const handleCancelSelection = () => {
    exitSelectionMode();
  };

  // Gestione seleziona tutti
  const handleSelectAll = () => {
    selectAll(messages);
  };

  // Selection mode header
  if (isSelectionMode) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-800">
        {/* Cancel button */}
        <button
          onClick={handleCancelSelection}
          className="p-2 rounded hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
          {...iconTip('Annulla selezione')}
        >
          <X className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        </button>

        {/* Selection count */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-purple-700 dark:text-purple-300">
            {selectedMessageIds.size} {selectedMessageIds.size === 1 ? 'messaggio selezionato' : 'messaggi selezionati'}
          </h3>
          <p className="text-xs text-purple-600/70 dark:text-purple-400/70">
            Tieni premuto Shift per selezionare un intervallo
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Select all */}
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 rounded transition-colors"
            {...tip('Seleziona tutti')}
          >
            <CheckSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Tutti</span>
          </button>

          {/* Forward to Team Chat button */}
          {onForwardToTeamChat && (
            <button
              onClick={() => {
                const selected = getSelectedMessages(messages);
                if (selected.length > 0) {
                  onForwardToTeamChat(selected);
                  exitSelectionMode();
                }
              }}
              disabled={selectedMessageIds.size === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#25D366] hover:bg-[#20bd5a] disabled:bg-[#25D366]/50 disabled:cursor-not-allowed text-white rounded transition-colors"
              {...tip('Inoltra a Team Chat')}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Team Chat</span>
            </button>
          )}

          {/* Analyze button */}
          <button
            onClick={handleAiButtonClick}
            disabled={selectedMessageIds.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white rounded transition-colors"
            {...tip('Analizza selezionati')}
          >
            <Sparkles className="w-4 h-4" />
            <span>Analizza</span>
          </button>
        </div>

        {/* Analyze with AI Modal */}
        {isAiAnalysisEnabled && wahaConfig?.configId && (
          <AnalyzeWithAIModal
            isOpen={showAnalyzeModal}
            onClose={() => {
              setShowAnalyzeModal(false);
              exitSelectionMode();
            }}
            chatJid={chat.id}
            configId={wahaConfig.configId}
            chatName={chat.name}
            selectedMessageIds={Array.from(selectedMessageIds)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-[#F7F7F5] dark:bg-gray-900 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
    <div className={`flex items-center ${compact ? 'gap-2 px-3' : 'gap-3 px-4'} py-3 min-w-0`}>
      {/* Torna all'elenco (visibile quando lista e chat non stanno affiancate) */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex-shrink-0 p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
          {...iconTip("Torna all'elenco delle chat", 'bottom')}
        >
          <ArrowLeft className="w-5 h-5 text-[#37352F] dark:text-white" />
        </button>
      )}

      {/* Avatar: stessa foto (e stessa cache) della riga in elenco */}
      <HeaderAvatar
        chatId={chat.id}
        imageUrl={chat.profilePicUrl}
        name={chat.name}
        isGroup={chat.isGroup}
        isOnline={presence?.status === 'online'}
        isTyping={presence?.status === 'typing' || presence?.status === 'recording'}
        size="md"
      />

      {/* Info */}
      <button
        className={`flex-1 min-w-0 text-left ${chat.isGroup ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
        onClick={chat.isGroup && groupsAvailable ? onInfoClick : undefined}
        disabled={!chat.isGroup || !groupsAvailable}
      >
        <h3 className="font-semibold text-[#37352F] dark:text-white truncate flex items-center gap-2">
          <span className="truncate">{chat.name}</span>
          <ProviderBadge serviceName={wahaConfig?.serviceName} size="xs" />
        </h3>
        <p className="text-xs text-[#6F6E69] dark:text-white/60 truncate">
          {chat.isGroup ? (
            participantNames.length > 0 ? (
              <span className="truncate block max-w-[300px]" {...tip(participantNames.join(', '))}>
                {participantNames.join(', ')}
              </span>
            ) : (
              <span>{chat.participantsCount || 0} partecipanti</span>
            )
          ) : (
            <PresenceText presence={presence} />
          )}
        </p>
      </button>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Info gruppo (solo numeri Standard) */}
        {chat.isGroup && groupsAvailable && onInfoClick && (
          <button onClick={onInfoClick} className="p-2 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors" {...iconTip("Info gruppo: partecipanti, nome, descrizione e link d'invito")}>
            <Info className="w-4 h-4 text-[#6F6E69] dark:text-white/70" />
          </button>
        )}

        {/* Search */}
        <button onClick={onSearchClick} className="p-2 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors" {...iconTip('Cerca nei messaggi')}>
          <Search className="w-4 h-4 text-[#6F6E69] dark:text-white/70" />
        </button>

        {/* Notes */}
        {onNotesClick && (
          <button onClick={onNotesClick} className="relative p-2 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors" {...iconTip(notesCount > 0 ? `Note (${notesCount})` : 'Note')}>
            <StickyNote className="w-4 h-4 text-[#6F6E69] dark:text-white/70" />
            {notesCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {notesCount > 9 ? '9+' : notesCount}
              </span>
            )}
          </button>
        )}

        {/* Ricontatta: promemoria (Task commerciale se la chat e' collegata a un Lead/Cliente) */}
        <RecontactHeaderButton chatKey={chat.id} chatName={chat.name || ''} organizationId={wahaConfig?.organizationId} compact={compact} />

        {/* CRM Panel toggle (compact mode) */}
        {onPanelToggle && (
          <button
            onClick={onPanelToggle}
            className={`p-2 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors ${isPanelOpen ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10' : ''}`}
            aria-pressed={!!isPanelOpen}
            {...iconTip(isPanelOpen ? 'Chiudi il pannello laterale' : 'Apri il pannello laterale: scheda e suggerimenti')}
          >
            {isPanelOpen ? <PanelRightClose className="w-4 h-4 text-[#6F6E69] dark:text-white/70" /> : <PanelRightOpen className="w-4 h-4 text-[#6F6E69] dark:text-white/70" />}
          </button>
        )}

        {/* Google Meet button */}
        <button onClick={() => setShowMeetModal(true)} className="p-2 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors" {...iconTip('Crea una videochiamata Google Meet')}>
          <Video className="w-4 h-4 text-[#6F6E69] dark:text-white/70" />
        </button>

        {/* Agent AI button */}
        {onAgentClick && (
          <button onClick={onAgentClick}
            {...(compact ? iconTip("Impostazioni dell'assistente AI per questa chat") : tip("Impostazioni dell'assistente AI per questa chat"))}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-[#37352F] dark:text-white/80 bg-[rgba(55,53,47,0.06)] dark:bg-white/8 hover:bg-[rgba(55,53,47,0.12)] dark:hover:bg-white/15 transition-colors border border-[rgba(55,53,47,0.10)] dark:border-white/10">
            <Bot className="w-3.5 h-3.5 text-[#37352F] dark:text-white/70" />
            {!compact && <span>Assistente</span>}
          </button>
        )}

        {/* Debug button (solo dev) — subito dopo Agent */}
        {onDebugClick && (
          <button onClick={onDebugClick}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors border border-emerald-200 dark:border-emerald-800">
            DBG
          </button>
        )}
      </div>
    </div>

    {/* Meta row: stato + operatore + labels */}
    {wahaConfig?.organizationId && (
      <div className="flex items-center gap-2 px-4 pb-2 flex-wrap">
        <ConversationStatusBadge status={currentStatus} onChange={handleStatusChange} />
        <OperatorAssignPicker assignedOperatorId={assignedOperatorId} members={orgMembers} onAssign={handleOperatorChange} />
        <LabelPicker chatJid={chat.id} organizationId={wahaConfig.organizationId} />
        {/* Per ultimo: e' il piu' lungo, se manca spazio va a capo solo lui (27/09/2026). */}
        <ClientBadge chatJid={chat.id} chatName={chat.name || ''} organizationId={wahaConfig.organizationId} />
      </div>
    )}

    {/* Modals */}
    {isAiAnalysisEnabled && wahaConfig?.configId && (
      <AnalyzeWithAIModal isOpen={showAnalyzeModal} onClose={() => setShowAnalyzeModal(false)}
        chatJid={chat.id} configId={wahaConfig.configId} chatName={chat.name} />
    )}
    <WhatsAppMeetModal isOpen={showMeetModal} onClose={() => setShowMeetModal(false)} chatName={chat.name} />
  </div>
  );
});

/** Avatar dell'intestazione: prima non chiedeva mai la foto e mostrava sempre le iniziali. */
function HeaderAvatar({ chatId, imageUrl, ...rest }: { chatId: string; imageUrl?: string } & Omit<ComponentProps<typeof ChatAvatar>, 'imageUrl'>) {
  const { profilePicUrl, ref } = useProfilePicture(chatId);
  return (
    <div ref={ref} className="flex-shrink-0">
      <ChatAvatar {...rest} imageUrl={imageUrl || profilePicUrl || undefined} />
    </div>
  );
}
